import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthShell, { Field, describeAuthError } from '../components/AuthShell.tsx';
import { ApiError } from '../lib/api.ts';
import { accountApi, acceptAuth } from '../lib/accountApi.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';

export default function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  useDocumentTitle('Sign in');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setUnconfirmed(false);
    try {
      acceptAuth(await accountApi.signIn(email, password));
      navigate('/dashboard', { replace: true });
    } catch (err) {
      // A password that is right but an address that is not yet confirmed is the
      // one failure worth offering a way out of.
      if (err instanceof ApiError && err.status === 403) setUnconfirmed(true);
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell eyebrow="Welcome back" title="Sign in">
      <form onSubmit={onSubmit} className="grid gap-5">
        <Field
          id="signin-email"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
        <Field
          id="signin-password"
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        {unconfirmed ? (
          <button
            type="button"
            className="justify-self-start text-sm text-petal hover:underline"
            onClick={() => void accountApi.resendVerification(email).catch(() => undefined)}
          >
            Send the confirmation link again
          </button>
        ) : null}

        <button className="btn btn-primary justify-self-start" disabled={busy || !email || !password}>
          {busy ? 'Checking…' : 'Sign in'}
        </button>
      </form>

      <div className="grid gap-1 text-sm text-muted">
        <p>
          <Link to="/forgot" className="text-petal hover:underline">
            Forgot your password?
          </Link>
        </p>
        <p>
          No account yet?{' '}
          <Link to="/signup" className="text-petal hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
